import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MDBDataTable } from 'mdbreact';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import LeftSidebar from './LeftSidebar';
import Topbar from './Topbar';
import Footer from './Footer';
import { BASE_URL } from '../Config';

const Payments = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [member, setMember] = useState(null);
  const [paying, setPaying] = useState(false);
  const token = localStorage.getItem('token');
  const memberId = localStorage.getItem('user_id') || localStorage.getItem('member');

  const [tableData, setTableData] = useState({
    columns: [
      { label: 'Sr.No.', field: 'serial', sort: 'asc' },
      { label: 'Payment ID', field: 'payment_id', sort: 'asc' },
      { label: 'Amount', field: 'amount', sort: 'asc' },
      { label: 'Status', field: 'status', sort: 'asc' },
      { label: 'Date', field: 'date', sort: 'asc' },
      { label: 'Receipt', field: 'receipt', sort: 'disabled' },
    ],
    rows: [],
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const userPaymentStatus = String(member?.payment_status || '').toLowerCase();
  const dueAmount = Number(member?.membership_fee || member?.payment || 0);

  // Calculate membership dates and renewal need
  const paymentDateStr = member?.membership_start_date || member?.payment_date;
  const endDateStr = member?.membership_end_date;
  let isExpired = false;
  let membershipStart = 'Not Available';
  let membershipEnd = 'Not Available';

  if (paymentDateStr && endDateStr) {
    const start = new Date(paymentDateStr);
    const end = new Date(endDateStr);
    if (!Number.isNaN(start.getTime())) {
      membershipStart = start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    if (!Number.isNaN(end.getTime())) {
      membershipEnd = end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      const today = new Date();
      isExpired = today > end;
    }
  } else if (paymentDateStr) {
    // Calculate fixed fiscal year range (1 April to 31 March)
    const d = new Date(paymentDateStr);
    if (!Number.isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = d.getMonth() + 1; // 1-12
      let startYear, endYear;
      if (month >= 4) {
        startYear = year;
        endYear = year + 1;
      } else {
        startYear = year - 1;
        endYear = year;
      }
      const start = new Date(`${startYear}-04-01`);
      const end = new Date(`${endYear}-03-31`);
      
      membershipStart = start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      membershipEnd = end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      
      const today = new Date();
      isExpired = today > end;
    } else {
      isExpired = true;
    }
  } else {
    isExpired = true;
  }

  const hasDueBill = member && (userPaymentStatus !== 'paid' || isExpired);

  const handlePayDueBill = async () => {
    if (!memberId || !token) {
      alert('Login details not found. Please login again.');
      return;
    }

    try {
      setPaying(true);
      const response = await axios.post(
        `${BASE_URL}/api/members/${memberId}/direct-payment-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.link) {
        window.location.href = response.data.link;
        return;
      }

      alert('Payment link is not available.');
    } catch (err) {
      console.error('Error creating payment link:', err);
      const message = err.response?.data?.message || 'Failed to create payment link.';
      alert(message);
    } finally {
      setPaying(false);
    }
  };

  const formatAmount = (amount) => {
    const value = Number(amount || 0);
    return value.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    });
  };

  const formatDate = (value) => {
    if (!value) return 'Not Available';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const statusBadge = (status) => {
    const text = status ? String(status).toUpperCase() : 'NA';
    const color = status === 'paid' ? '#198754' : status === 'pending' ? '#f59e0b' : '#6c757d';

    return (
      <span
        style={{
          color: '#fff',
          backgroundColor: color,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          display: 'inline-block',
        }}
      >
        {text}
      </span>
    );
  };

  useEffect(() => {
    const fetchPayments = async () => {
      if (!memberId || !token) {
        setError('Login details not found. Please login again.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BASE_URL}/api/member_payment_list/${memberId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = response.data.list || [];
        setMember(response.data.member || null);
        setTableData((prevData) => ({
          ...prevData,
          rows: list.map((row, index) => ({
            serial: index + 1,
            payment_id: row.payment_id || 'Not Available',
            amount: formatAmount(row.amount),
            status: statusBadge(row.status),
            date: formatDate(row.date || row.created_at),
            receipt: (
              <a
                href={`/members/receipt?payment_id=${row.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-primary"
                title="Download receipt"
              >
                <i className="fa fa-download"></i> Download
              </a>
            ),
          })),
        }));
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError('Failed to load payments.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [memberId, token]);

  return (
    <>
      <div id="page-top">
        <div id="wrapper">
          <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
          <div id="content-wrapper" className="d-flex flex-column">
            <div id="content">
              <Topbar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
              <div className="container-fluid">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h4 className="mb-0">Payments</h4>
                    {member && (
                      <small className="text-muted">
                        {member.name} {member.membership ? `| ${member.membership}` : ''}
                      </small>
                    )}
                  </div>
                  {!loading && hasDueBill && !isExpired && (
                    <Button variant="success" onClick={handlePayDueBill} disabled={paying}>
                      <i className="fa fa-credit-card"></i>{' '}
                      {paying ? 'Please wait...' : `Pay Due Bill${dueAmount > 0 ? ` (${formatAmount(dueAmount)})` : ''}`}
                    </Button>
                  )}
                </div>

                {!loading && isExpired && (
                  <div className="alert alert-warning border-left-warning shadow-sm rounded-3 mb-4 d-flex align-items-center justify-content-between py-3 px-4">
                    <div style={{ textAlign: 'left' }}>
                      <h5 className="alert-heading text-warning-emphasis mb-1 fw-bold" style={{ color: '#856404' }}>⚠️ Annual Membership Renewal Due</h5>
                      <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        Your annual membership has ended {membershipEnd !== 'Not Available' ? `(Expired on: ${membershipEnd})` : ''}.
                        Please renew your membership to continue active access.
                      </p>
                    </div>
                    <Button variant="warning" className="text-white fw-bold px-4" onClick={handlePayDueBill} disabled={paying}>
                      <i className="fa fa-refresh"></i> Renew Membership
                    </Button>
                  </div>
                )}

                {loading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="grow" size="sm" variant="primary" />
                    <Spinner animation="grow" size="sm" variant="primary" />
                    <Spinner animation="grow" size="sm" variant="primary" />
                  </div>
                ) : error ? (
                  <div className="alert alert-danger">{error}</div>
                ) : (
                  <MDBDataTable striped bordered small data={tableData} noBottomColumns />
                )}
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      <a className="scroll-to-top rounded" href="#page-top">
        <i className="fas fa-angle-up"></i>
      </a>
    </>
  );
};

export default Payments;
